import { useQuery } from '@apollo/client';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { isValidFiSerialNumber, isValidThirdPartySerialNumber } from '@barkinglabs/nano-shared';

import { gqlTypes } from '../../types/gqlTypes';
import { searchResellerChipsQuery } from './ResellerSearchResults.graphql';
import ChipList from './ChipList';
import StatusTypeMap from './StatusTypeMap';
import { useAppViewContext } from '../lib/AppViewContext';
import ChipListHeader from './ChipListHeader';
import { chipDetailsQuery } from '../ChipDetails/ChipDetails.graphql';
import EmptyChipList from './EmptyChipList';
import { ReassignFiChipModal } from './ReassignFiChipModal';
import { RegisterThirdPartyChipModal } from './RegisterThirdPartyChipModal';
import SearchResultsHeader from './SearchResultsHeader';

export interface ResellerSearchResultsProps {
  query: string;
  setSearchAction: React.Dispatch<React.SetStateAction<string>>;
  setHasOwnerInfo: React.Dispatch<React.SetStateAction<boolean>>;
  searchAction?: string;
  onQueryCleared?: () => void;
}

const MIN_LEN = 3;

function useBusyDelay(busy: boolean, delay = 300) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (busy) {
      const t = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(t);
    }
    setShow(false);
  }, [busy, delay]);
  return show;
}

type SearchAction = '' | 'VIEW_CHIP' | 'REGISTER_NON_FI_CHIP' | 'REASSIGN_FI_CHIP';

function computeSearchAction(opts: {
  isTooShort: boolean;
  loading: boolean;
  chipDetailsLoading: boolean;
  chipsLen: number;
  looksLikeFi: boolean;
  looksLikeThirdParty: boolean;
  hasChipDetails: boolean;
  isVerifiedReseller: boolean;
}): SearchAction {
  const {
    isTooShort,
    loading,
    chipDetailsLoading,
    chipsLen,
    looksLikeFi,
    looksLikeThirdParty,
    hasChipDetails,
    isVerifiedReseller,
  } = opts;

  if (isTooShort) {
    return '';
  }
  if (loading || chipDetailsLoading) {
    return '';
  }
  if (chipsLen > 0) {
    return '';
  }

  const looksLikeSerial = looksLikeFi || looksLikeThirdParty;
  if (!looksLikeSerial) {
    return '';
  }

  // If the chip exists, show option to reassign (if verified)
  if (isVerifiedReseller && hasChipDetails) {
    return 'REASSIGN_FI_CHIP';
  }

  // If the chip exists but is not a Fi chip, show option to view it
  if (hasChipDetails) {
    return 'VIEW_CHIP';
  }

  // If it's a 3rd-party serial and not found, show option to register
  if (looksLikeThirdParty) {
    return 'REGISTER_NON_FI_CHIP';
  }

  return '';
}

export default function ResellerSearchResults({
  query,
  setSearchAction,
  setHasOwnerInfo,
  onQueryCleared,
}: ResellerSearchResultsProps) {
  const { isDesktop, setSelectedStatus } = useAppViewContext();

  const handleReassignSuccess = useCallback(
    (clearOwnerInfo: boolean) => {
      const targetStatus = clearOwnerInfo ? gqlTypes.ChipStatusType.SHIPPED : gqlTypes.ChipStatusType.ACTIVE;
      setSelectedStatus(targetStatus);
      onQueryCleared?.();
    },
    [setSelectedStatus, onQueryCleared],
  );

  const q = (query ?? '').trim();
  const isTooShort = q.length < MIN_LEN;

  const [resolvedQuery, setResolvedQuery] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const settledForThisQuery = resolvedQuery === q;

  // A registered third-party chip lands in Stock, so send the user there to see it.
  const handleRegisterClosed = useCallback(
    (success?: boolean) => {
      setShowRegisterModal(false);
      if (success) {
        setSelectedStatus(gqlTypes.ChipStatusType.SHIPPED);
        onQueryCleared?.();
      }
    },
    [setSelectedStatus, onQueryCleared],
  );

  // Track last non-empty results to show only when the user is shortening the query
  const lastNonEmptyRef = useRef<{ q: string; chips: any[] } | null>(null);

  // Search without statusType to get results across all tabs
  const searchInput = useMemo(
    () => ({
      shortId: q,
      serialNumber: q,
      searchTerm: q,
    }),
    [q],
  );

  const variables = useMemo(() => ({ input: searchInput }), [searchInput]);

  const { data, loading } = useQuery<gqlTypes.searchResellerChips, gqlTypes.searchResellerChipsVariables>(
    searchResellerChipsQuery,
    {
      variables,
      skip: isTooShort,
      notifyOnNetworkStatusChange: true,
      onCompleted: () => setResolvedQuery(q),
    },
  );

  const allChips = data?.currentUser.chipReseller?.searchChips.chips ?? [];
  const isVerifiedReseller = data?.currentUser.chipReseller?.verified ?? false;

  // Use all chips for unified search results
  const chips = allChips;

  // Update 'last non-empty' cache only when a query settles with results
  useEffect(() => {
    if (!loading && chips.length > 0 && settledForThisQuery) {
      lastNonEmptyRef.current = { q, chips };
    }
  }, [loading, chips, settledForThisQuery, q]);

  const shouldShowCachedWhileLoading = useMemo(() => {
    if (!loading) return false;
    const last = lastNonEmptyRef.current;
    if (!last) return false;
    return q.length < last.q.length && last.q.startsWith(q);
  }, [loading, q]);

  const displayChips = shouldShowCachedWhileLoading && lastNonEmptyRef.current ? lastNonEmptyRef.current.chips : chips;

  const looksLikeFi = isValidFiSerialNumber(q);
  const looksLikeThirdParty = isValidThirdPartySerialNumber(q);

  // If no results and query looks like serial - check details
  const shouldCheckChipDetails = !isTooShort && !loading && chips.length === 0 && (looksLikeFi || looksLikeThirdParty);

  const { data: chipDetails, loading: chipDetailsLoading } = useQuery<gqlTypes.chipDetails>(chipDetailsQuery, {
    variables: { id: Number(q) },
    skip: !shouldCheckChipDetails,
    fetchPolicy: 'network-only',
    onCompleted: () => setResolvedQuery(q),
  });

  const derivedAction = useMemo(
    () =>
      computeSearchAction({
        isTooShort,
        loading,
        chipDetailsLoading,
        chipsLen: chips.length,
        looksLikeFi,
        looksLikeThirdParty,
        hasChipDetails: Boolean(chipDetails),
        isVerifiedReseller,
      }),
    [
      isTooShort,
      loading,
      chipDetailsLoading,
      chips.length,
      looksLikeFi,
      looksLikeThirdParty,
      chipDetails,
      isVerifiedReseller,
    ],
  );

  // Only update when it actually changes (avoids extra renders)
  useEffect(() => {
    setSearchAction(derivedAction);
  }, [derivedAction, setSearchAction]);

  // Compute and propagate hasOwnerInfo based on chip details
  const derivedHasOwnerInfo = useMemo(() => {
    const chip = chipDetails?.chip;
    if (chip?.__typename === 'AssignedChip') {
      return chip.latestContactHistoryEntry !== null && chip.latestContactHistoryEntry !== undefined;
    }
    if (chip?.__typename === 'StrangerChip') {
      return chip.hasOwner;
    }
    return false;
  }, [chipDetails]);

  useEffect(() => {
    setHasOwnerInfo(derivedHasOwnerInfo);
  }, [derivedHasOwnerInfo, setHasOwnerInfo]);

  // Delayed loader (prevents quick flashes)
  const delayedLoading = useBusyDelay(loading || chipDetailsLoading, 300);

  // Show 'No results' only when the current query has fully settled
  const showNoResults = !isTooShort && !loading && !chipDetailsLoading && settledForThisQuery && chips.length === 0;

  if (isTooShort) {
    return (
      <>
        <SearchResultsHeader query={q} onClear={onQueryCleared} />
        <ChipListHeader searchMode={true} />
        <EmptyChipList searchMode={true} query={q} />
      </>
    );
  }

  // If loading and we do NOT want to show cached results (i.e., user is extending the query),
  // show a delayed loader. (If shortening, we keep cached results visible instead.)
  if (delayedLoading && !shouldShowCachedWhileLoading) {
    return (
      <>
        <SearchResultsHeader query={q} onClear={onQueryCleared} />
        <ChipListHeader searchMode={true} />
        <div>Searching…</div>
      </>
    );
  }

  // Fix flash: if loading with no displayChips, show header only (with optional delayed loader)
  if (loading && displayChips.length === 0) {
    return (
      <>
        <SearchResultsHeader query={q} onClear={onQueryCleared} />
        <ChipListHeader searchMode={true} />
        {delayedLoading && <div>Searching…</div>}
      </>
    );
  }

  if (showNoResults) {
    const isExistingChip = Boolean(chipDetails);
    return (
      <>
        {showReassignModal && (
          <ReassignFiChipModal
            serialNumber={q}
            hasOwnerInfo={derivedHasOwnerInfo}
            onClose={() => {
              setShowReassignModal(false);
            }}
            onSuccess={handleReassignSuccess}
            refetchQueries={[{ query: searchResellerChipsQuery, variables }]}
          />
        )}
        {showRegisterModal && <RegisterThirdPartyChipModal serialNumber={q} onClose={handleRegisterClosed} />}
        <SearchResultsHeader query={q} onClear={onQueryCleared} />
        <ChipListHeader searchMode={true} />
        <EmptyChipList
          searchMode={true}
          query={q}
          isExistingChip={isExistingChip}
          isVerifiedReseller={isVerifiedReseller}
          onReassignClick={() => setShowReassignModal(true)}
          onRegisterClick={() => setShowRegisterModal(true)}
        />
      </>
    );
  }

  return (
    <div>
      <SearchResultsHeader query={q} onClear={onQueryCleared} />
      <ChipList
        chips={displayChips}
        statusTypeMap={StatusTypeMap.fromResults(displayChips)}
        refetchQueries={[{ query: searchResellerChipsQuery, variables }]}
        searchMode={true}
        query={q}
      />
    </div>
  );
}
